import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Post } from '../models/post';
import { Comment } from '../models/comment.js';
import { IGenericJSON } from '../helpers/interface';
import { WebsocketService } from './websocket.service';

@Injectable({ providedIn: 'root' })
export class PostService {
  private http: HttpClient = inject(HttpClient);
  private wsService: WebsocketService = inject(WebsocketService);

  constructor() {}

  /**
   * Emits whenever a post (or one of its comments/replies) is created,
   * updated, or deleted anywhere, so an open feed can refetch and stay
   * live. The payload is deliberately just a signal (post_id) rather than
   * the post itself — visibility filtering (grade level, counselor
   * linkage, interests) already lives in getPosts() on the server, and
   * duplicating it into the socket broadcast would mean maintaining two
   * copies of that logic.
   * @returns {Observable<{ post_id: number }>}
   */
  onPostChanged(): Observable<{ post_id: number }> {
    return this.wsService.on<{ post_id: number }>('post:changed');
  }

  /**
   * Uploads an image file to the server and returns the saved URL.
   * @param {FormData} formData - The form data containing the image file.
   * @returns {Observable<any>}
   */
  sendImg(formData: any): Observable<any> {
    return this.http.post('/api/upload', formData);
  }

  /**
   * Fetches a paginated list of counselor posts.
   * @param {number} limit - Maximum number of posts to return. Defaults to 10.
   * @param {number} offset - Number of posts to skip for pagination. Defaults to 0.
   * @param {string} q - Optional content search query.
   * @param {number[]} grades - Optional grade levels to filter by.
   * @param {string} status - Optional status filter: '' (default, everything but deleted), 'draft', or 'published'.
   * @returns {Observable<Post[]>}
   */
  getPosts(limit = 10, offset = 0, q = '', grades: number[] = [], status = ''): Observable<Post[]> {
    let params = new HttpParams().set('limit', limit).set('offset', offset);
    if (q.trim()) params = params.set('q', q.trim());
    if (grades.length) params = params.set('grades', grades.join(','));
    if (status) params = params.set('status', status);

    return this.http
      .get<IGenericJSON[]>('/api/posts', { params })
      .pipe(map((data) => Post.digestResponse(data)));
  }

  /**
   * Creates a new post.
   * @param {any} post - The post payload including content and optional audience filters.
   * @returns {Observable<Post>}
   */
  createPost(post: any): Observable<Post> {
    return this.http.post<IGenericJSON>('api/posts', post).pipe(map((data) => Post.digest(data)));
  }
  /**
   * Updates a post.
   * @param {number} id
   * @param {any} post - The post payload including content and optional audience filters.
   * @returns {Observable<Post>}
   */
  updatePost(id: number, post: any): Observable<Post> {
    return this.http
      .put<IGenericJSON>(`/api/posts/${id}`, post)
      .pipe(map((data) => Post.digest(data)));
  }

  /**
   * Deletes a post.
   * @param {number} id
   * @param {any} post
   * @returns {Observable<Post>}
   */
  deletePost(id: number, post: any): Observable<Post> {
    return this.http
      .put<IGenericJSON>(`/api/posts/delete/${id}`, post)
      .pipe(map((data) => Post.digest(data)));
  }

  /**
   * Fetches paginated comments for a post, including nested replies.
   * @param {any} post_id - The ID of the post to fetch comments for.
   * @param {number} limit - Maximum number of comments to return. Defaults to 10.
   * @param {number} offset - Number of comments to skip for pagination. Defaults to 0.
   * @returns {Observable<Comment[]>}
   */
  getComments(post_id: any, limit = 10, offset = 0): Observable<Comment[]> {
    return this.http
      .get<IGenericJSON[]>(`/api/posts/comments`, {
        params: {
          post_id,
          limit,
          offset,
        },
      })
      .pipe(map((data) => Comment.digestResponse(data)));
  }

  /**
   * Creates a new comment on a post, optionally as a reply to an existing comment.
   * @param {number} postId - The ID of the post to comment on.
   * @param {string} content - The text content of the comment.
   * @param {number} [parent_id] - The ID of the parent comment if this is a reply.
   * @returns {Observable<any>}
   */
  createComment(postId: number, content: string, parent_id?: number): Observable<any> {
    return this.http.post(`/api/posts/comments`, {
      post_id: postId,
      content,
      parent_id: parent_id || null,
    });
  }
}
